/**
 * Shared copy for the expert "On your honor" toggle.
 *
 * Honor-system self-attestation, a single on/off toggle (no reason picker).
 * Copy splits by moment:
 * - `helper` (hover / screen-reader) and `introMessage` (one-time banner) state
 *   the deferential framing and the doubled-pay mechanic where the feature is
 *   introduced, then it is never nagged after.
 * - `ON_YOUR_HONOR_CONFIRMATIONS` is the acknowledgement shown as a one-off
 *   gratitude confirmation when the inquiry is marked — gratitude only; the
 *   payment lives in the banner.
 */

export const ON_YOUR_HONOR_COPY = {
  // Display label is "On your honor", carrying the trust ethos on the toggle
  // itself; the "extra effort" framing lives in the supporting copy.
  action: 'On your honor',
  confirmedLabel: 'On your honor',
  helper:
    "When an inquiry takes extra effort, mark it on your honor: we trust you and respect your time, and we'll double your honorarium.",
  /**
   * One-time composer banner shown above the composer (see ChatContent). It
   * composes from three pieces so the emphasized chip can sit inline: the
   * `introBadge` badge, then `introLeadIn` + the "On your honor" chip +
   * `introMessage`, reading "Use [On your honor] when an inquiry takes extra
   * effort. We trust your judgment and we'll double your honorarium."
   */
  introBadge: 'New',
  introLeadIn: 'Use',
  introMessage:
    "when an inquiry takes extra effort. We trust your judgment and we'll double your honorarium.",
  /**
   * Reason sheet, shown to the cohort where a reason is required. Pressing the
   * toggle opens it *before* anything is marked; choosing a reason is what
   * applies the mark. Framed around the effort the expert put in, not how good
   * the answer was: this is deliberately not a grading rubric.
   *
   * Escape is the host's X (plus Esc / backdrop), because a modal with no way out
   * is an accessibility violation (APG dialog Esc; WCAG 2.1.2). There is no
   * second in-body exit — a "close without marking" link is a Skip control
   * however it is worded, and answering is meant to be the only path that marks.
   */
  reasonPrompt: 'What made this one take extra?',
  /**
   * One-line "why we ask", framed as understanding — not grading or pay. Says
   * nothing about the mark being contingent: that is what the ask *requires*,
   * while this is why we *want* it.
   *
   * "answers like this" rather than "your best answers", since praising quality
   * beneath an effort question reframes the sheet as a rating.
   */
  reasonWhy: 'It helps us understand the work behind answers like this.',
  reasonCustomPlaceholder: 'Something else…',
  reasonCustomSubmitA11yLabel: 'Add your reason',
  reasonCloseA11yLabel: 'Close without marking this inquiry',
} as const;

/**
 * Effort reasons offered as single-tap chips in the post-selection sheet. These
 * answer "why did a strong answer take more work?" — deliberately effort
 * categories, not answer-quality or grading-rubric dimensions. The `id`s are the
 * answer values the backend validates against the published `oyh-reason` survey
 * schema, so they must match its choice values; adding or removing a reason
 * means publishing a new survey version. Labels are display-only — the app
 * renders its own chips, so their wording can change freely. The write-in field
 * covers anything outside the set.
 */
export const ON_YOUR_HONOR_REASONS = [
  { id: 'complex_case', label: 'Complex case' },
  { id: 'case_review', label: 'Reviewing details' },
  { id: 'extra_research', label: 'Extra research' },
  { id: 'extra_back_and_forth', label: 'Follow-up questions' },
] as const;

export type OnYourHonorReasonId = (typeof ON_YOUR_HONOR_REASONS)[number]['id'];

/**
 * Slug of the published SurveyJS definition the reason survey submits against.
 * Must match the survey created in the admin/CLI; the app fetches the current
 * version by this slug and posts answers keyed by the fields below.
 */
export const OYH_REASON_SURVEY_SLUG = 'oyh-reason';

/**
 * SurveyJS question names in the `oyh-reason` schema. A chip records
 * `{ [REASON]: <chip id> }`; the write-in records `{ [REASON_OTHER]: <text> }`.
 * These must match the definition's element names or the backend rejects the
 * answers as failing validation.
 */
export const OYH_REASON_ANSWER_FIELD = 'reason';
export const OYH_REASON_OTHER_ANSWER_FIELD = 'reason_other';

/**
 * Subject linkage stamped on the reason response so it ties to the inquiry it
 * was marked on (and powers the "already answered, don't re-ask" gate). The id
 * is the inquiry's own id, not its conversation id.
 */
export const OYH_REASON_SUBJECT_TYPE = 'inquiry';

/**
 * Thank-you lines shown once a mark lands, rotating so consecutive marks differ.
 * Each is a template over the expert's salutation (e.g. "Dr. Lin") — the name is
 * woven inline when available and the line degrades to a clean no-name version
 * when it isn't. Gratitude only — trust is implied by not questioning, and the
 * payment is not restated (the banner + helper carry that).
 *
 * Two surfaces render these. The reason sheet replaces its body with the line
 * for a beat before closing, so the thanks lands where the expert is already
 * looking; marks that never open a sheet (cohort off, already answered, re-mark)
 * get it as a toast instead, so none goes unacknowledged.
 *
 * Keep the four distinct in thought, not just wording: "going the extra mile"
 * was retired for being the same idiom as "going above and beyond". The longest
 * line also sets `THANKS_HOLD_MS` in `OnYourHonorReasonContent`, so a longer one
 * means revisiting it.
 */
export const ON_YOUR_HONOR_CONFIRMATIONS: ((salutation: string) => string)[] = [
  (name) => (name ? `We appreciate you, ${name}.` : 'We appreciate you.'),
  (name) => (name ? `${name}, thank you for taking the time.` : 'Thank you for taking the time.'),
  (name) =>
    name
      ? `Thank you for going above and beyond, ${name}.`
      : 'Thank you for going above and beyond.',
  (name) => (name ? `${name}, thank you for the extra effort.` : 'Thank you for the extra effort.'),
];
